import { getIn, useFormikContext } from 'formik';
import * as React from 'react';

import { RadioGroup } from '@/design-system/components/ui';

export interface FormikRadioGroupProps {
  name: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const FormikRadioGroup = React.forwardRef<HTMLDivElement, FormikRadioGroupProps>(
  ({ name, children, disabled, className }, ref) => {
    const { values, errors, touched, setFieldValue } = useFormikContext();

    const value = getIn(values, name);
    const error = getIn(errors, name);
    const fieldTouched = getIn(touched, name);

    const errorId = React.useId();
    const hasError = Boolean(fieldTouched && error);
    const errorMessage = fieldTouched && typeof error === 'string' ? error : undefined;

    const handleValueChange = React.useCallback((val: string) => setFieldValue(name, val), [name, setFieldValue]);

    return (
      <div>
        <RadioGroup
          ref={ref}
          value={String(value ?? '')}
          onValueChange={handleValueChange}
          disabled={disabled}
          className={className}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : undefined}
        >
          {children}
        </RadioGroup>
        {errorMessage && (
          <p id={errorId} className="tw:mt-1 tw:text-sm tw:font-inter tw:text-red-500" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  },
);

FormikRadioGroup.displayName = 'FormikRadioGroup';

export { FormikRadioGroup };
